import { useAuth, useInboxConversation } from "@tellescope/tanstack-query";
import { ReversePaginatedList, Spinner } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { ZendeskMessage } from "./ZendeskMessage";
import { useMemo } from "react";

export interface InboxConversationZendeskListProps {
  threadId: string;
  conversationData?: ReturnType<typeof useInboxConversation>;
}

export const InboxConversationZendeskList = ({ threadId, conversationData }: InboxConversationZendeskListProps) => {
  const { t } = useTranslation("inbox");
  const { currentUser } = useAuth();

  const internalConversationData = useInboxConversation(threadId, { enabled: !conversationData });
  const { metadata, messages, scheduled, loadNextPage, hasNextPage, isLoading, markReadOrUnread } =
    conversationData ?? internalConversationData;

  const allMessages = useMemo(() => [...messages, ...scheduled], [messages, scheduled]);

  const isUnread = currentUser.id ? !metadata?.readBy?.[currentUser.id] : false;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  if (!metadata) {
    <div className="flex items-center justify-center h-full">
      <p className="text-destructive">{t("There was an error loading this thread...")}</p>
    </div>;
  }

  if (allMessages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 text-center">
        <div className="text-sm text-muted-foreground">{t("No comments in this ticket yet")}</div>
      </div>
    );
  }

  return (
    <ReversePaginatedList
      className="flex-1 overflow-y-auto pb-4 space-y-4"
      onTopReached={() => {
        if (hasNextPage) {
          loadNextPage();
        }
      }}
      onBottomReached={() => {
        if (isUnread && !!currentUser) {
          markReadOrUnread({ userId: currentUser.id, readStatus: "read" });
        }
      }}
      onFocusWithin={({ isAtBottom }) => {
        if (isAtBottom && isUnread && currentUser) {
          markReadOrUnread({ userId: currentUser.id, readStatus: "read" });
        }
      }}
      onClickWithin={({ isAtBottom }) => {
        if (isAtBottom && isUnread && currentUser) {
          markReadOrUnread({ userId: currentUser.id, readStatus: "read" });
        }
      }}
    >
      {allMessages.map((message) => (
        <ZendeskMessage key={message.id} message={message} className="px-4" />
      ))}
    </ReversePaginatedList>
  );
};
